import type { VendorCitationData } from '@/components/ui/citation-chip';
import type { EditorCitation } from '@/components/report-editor';
import { ApiError } from '@/lib/api/client';
import type {
  ReportCitation,
  ReportFit,
  ReportProseIssue,
  ReportVersion,
  VendorReport,
} from '@/lib/api/vendor-intelligence-report';

/**
 * The Report step's rules, kept pure (T-0269, T-0271; the pages of T-0283
 * are in `report-model.ts`): what a citation chip says, what the prose
 * checks say, and why Publish waits. Every figure printed here is the
 * server's, formatted.
 */

/** The figure chips' hover text. */
export const TOKEN_SOURCE = 'From the confirmed ratings, frozen when the report was drafted';

export const REFUSAL_FLAG =
  'The AI could not draft this from cited evidence. Write it yourself before publishing.';

/** One decimal, as the fit summary prints an index or a coverage share. */
export function score(value: number): string {
  return value.toFixed(1);
}

/**
 * The chip's label: the record id the report prints (v2's `display`), the
 * record's own number (VK-00905, a requirement's), "Rating: <area>" for a
 * confirmed rating, else its label, shortened. The citation dialog shows
 * the whole of it.
 */
export function citationLabel(
  c: ReportCitation,
  moduleName: (id: string) => string | undefined = () => undefined,
): string {
  if (c.display) return c.display;
  if (c.code) return c.code;
  const area = c.module_id ? moduleName(c.module_id) : undefined;
  if (c.kind === 'vendor_area_rating' && area) return `Rating: ${area}`;
  const label = c.label.trim();
  return label.length > 48 ? `${label.slice(0, 45)}…` : label;
}

export function toEditorCitation(
  c: ReportCitation,
  moduleName?: (id: string) => string | undefined,
): EditorCitation {
  return { id: c.key, label: citationLabel(c, moduleName) };
}

/** A module's name from the fit frozen with the draft. */
export function moduleNamer(fit: ReportFit | null | undefined): (id: string) => string | undefined {
  const names = new Map((fit?.modules ?? []).map((m) => [m.module_id, m.name]));
  return (id) => names.get(id);
}

/** A citation as the shared quote primitive shows it: the excerpt, then where it is from. */
export function toVendorCitation(c: ReportCitation): VendorCitationData {
  return {
    text: c.label,
    parts: {
      ...(c.code ? { number: c.code } : {}),
      ...(c.excerpt !== null ? { excerpt: c.excerpt } : {}),
      source_label: c.source_label,
      ...(c.url ? { url: c.url } : {}),
      date: c.date ?? '',
      ...(c.locator_label ? { locator_label: c.locator_label } : {}),
      confidential: c.confidential,
    },
  };
}

export interface ProseIssueLine {
  blockId: string;
  sectionTitle: string;
  text: string;
}

/** One advisory check in plain words. */
export function proseIssueText(issue: ReportProseIssue): string {
  switch (issue.kind) {
    case 'typed_percent':
      return `“${issue.text}” is a percentage typed in the text, not a figure from the ratings`;
    case 'typed_figure':
      return `“${issue.text}” is a number typed in the text, not a figure from the ratings`;
    case 'unknown_placeholder':
      return `${issue.text} is not a figure in this report`;
    default:
      return `“${issue.text}” needs a look`;
  }
}

/** The draft's prose checks, block by block in reading order (`sectionTitle` is the page's title). */
export function proseIssueLines(
  pages: ReadonlyArray<{
    title: string;
    blocks: ReadonlyArray<{ id: string; issues: ReportProseIssue[] }>;
  }>,
): ProseIssueLine[] {
  const out: ProseIssueLine[] = [];
  for (const s of pages) {
    for (const b of s.blocks) {
      for (const issue of b.issues) {
        out.push({ blockId: b.id, sectionTitle: s.title, text: proseIssueText(issue) });
      }
    }
  }
  return out;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Why Publish is not available yet, in plain words; null when it is. */
export function publishBlocker(report: VendorReport): string | null {
  const draft = report.draft;
  if (!draft) return 'Prepare the report first.';
  if (draft.refusals > 0) {
    return `${plural(draft.refusals, 'paragraph')} still ${draft.refusals === 1 ? 'needs' : 'need'} writing: the AI could not draft ${draft.refusals === 1 ? 'it' : 'them'} from cited evidence.`;
  }
  return null;
}

export type PdfState = 'ready' | 'rendering' | 'failed' | 'none';

export function pdfState(version: ReportVersion): PdfState {
  if (!version.pdf) return 'none';
  if (version.pdf.render_status === 'succeeded') return 'ready';
  if (version.pdf.render_status === 'failed') return 'failed';
  return 'rendering';
}

export const PDF_STATE_LABEL: Record<PdfState, string> = {
  ready: 'PDF ready',
  rendering: 'PDF rendering',
  failed: 'PDF failed',
  none: 'No PDF yet',
};

/** The reason an API error gives in its details, if any. */
export function errorReason(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const reason = err.details?.reason;
  return typeof reason === 'string' ? reason : null;
}

/**
 * A refused save in plain words: the guards' sentences when the server
 * sent them (`report.block_guard`), else the error as it came.
 */
export function saveError(err: unknown): unknown {
  if (err instanceof ApiError && errorReason(err) === 'report.block_guard') {
    const issues = Array.isArray(err.details?.issues) ? err.details.issues : [];
    const words = issues
      .map((i) =>
        i && typeof i === 'object' && typeof (i as { message?: unknown }).message === 'string'
          ? (i as { message: string }).message
          : null,
      )
      .filter((m): m is string => m !== null);
    if (words.length > 0) return new Error(words.join(' '));
  }
  return err;
}
